import type { Company } from "@repo/app-types/entities/company";
import { InterviewRetakeMode } from "@repo/app-types/entities/jobType";
import type {
  ProjectUpdate,
  ProjectWithInterviewCount,
  ProjectWithJobType,
} from "@repo/app-types/entities/project";
import {
  getInterviewLinkForProject,
  getProjectAndJobTypeProperty,
  JobTypeProperty,
} from "@repo/app-types/entities/project";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { showToast, ToastVariant } from "@repo/ui/toast/toast";
import { useUpdateJobTypeMutation } from "../../../../api/entities/jobType.js";
import { AppearanceSettingsSection } from "../appearanceSettingsSection.js";
import { InterviewLanguageSetting } from "../interviewLanguageSetting.js";
import { InterviewLinkCopybox } from "../interviewLinkCopybox.js";
import { InterviewRetakeSetting } from "../interviewRetakeSetting.js";
import { InterviewSimulationButton } from "../interviewSimulation/interviewSimulationButton.js";
import { MessagingAutomationSection } from "../messagingAutomationSection.js";
import { ProjectNotificationsSettingsSection } from "../projectNotificationsSettingsSection.js";
import { JobOpeningPreInterviewFormSetting } from "../recruiting/jobOpeningPreInterviewFormSetting.js";
import { ChapterGradingResultsEmailSetting } from "./chapterGradingResultsEmailSetting.js";
import { LessonOrderSetting } from "./lessonOrderSetting.js";
import { PlaybookSetting } from "./playbookSetting.js";
import { TrainingMaterialsSection } from "./trainingMaterialsSection.js";

interface TrainingProjectGeneralSettingsProps {
  project: ProjectWithInterviewCount & ProjectWithJobType;
  company: Company;
  onSaveProject: (project: ProjectUpdate) => Promise<void>;
}

export const TrainingProjectGeneralSettings = ({
  project,
  company,
  onSaveProject,
}: TrainingProjectGeneralSettingsProps) => {
  const [updateJobType] = useUpdateJobTypeMutation();

  const language = getProjectAndJobTypeProperty(
    project,
    JobTypeProperty.Language,
  );
  const multilangLanguages = getProjectAndJobTypeProperty(
    project,
    JobTypeProperty.MultilangLanguages,
  );

  const voice = getProjectAndJobTypeProperty(
    project,
    JobTypeProperty.LanguageVoice,
  );

  const interviewRetakeSettings =
    project.job_type.settings.interview_retake_settings;

  return (
    <div className="mx-auto flex w-full max-w-4xl flex-col space-y-6 pt-6">
      <div>
        <div className="rounded-lg border border-border-light bg-white px-4 py-3">
          <div className="mb-2 flex items-end space-x-4">
            <div className="shrink-0">
              <InterviewLinkCopybox
                inviteLinkUrl={getInterviewLinkForProject(
                  import.meta.env.VITE_INTERVIEW_APP_BASE_URL,
                  company,
                  project,
                )}
                linkLabel="Learning journey link"
                project={project}
                onCopyInterviewLink={() => {
                  showToast({
                    title: "Learning journey link copied to clipboard",
                    variant: ToastVariant.Success,
                  });
                }}
              />
            </div>
            <div className="shrink-0">
              <InterviewSimulationButton project={project} />
            </div>
          </div>
          <div className="mb-2 text-sm text-foreground">
            Send the learning journey link to trainees or add it to your
            training materials. Links work on desktop or mobile browsers.
          </div>
        </div>
      </div>

      <div className="flex flex-col space-y-3">
        <div>
          <Label size={Size.LARGE}>Training configuration</Label>
          <div className="flex flex-col space-y-3">
            <PlaybookSetting
              playbookId={project.job_type.settings.playbook_id}
              onSave={(playbookId) => {
                void updateJobType({
                  id: project.job_type.id,
                  settings: {
                    ...project.job_type.settings,
                    playbook_id: playbookId,
                  },
                });
              }}
              allowsEmptySelection={false}
              usesEffectivePlaybookId={false}
            />

            {checkHasLegacyTrainingMaterials(project) ? (
              <TrainingMaterialsSection jobType={project.job_type} />
            ) : null}
          </div>
        </div>

        <InterviewLanguageSetting
          language={language}
          multilangLanguages={multilangLanguages}
          voice={voice}
          isTrainingProject
          onSave={(newLanguage, newMultilangLanguages, newVoice) => {
            void updateJobType({
              id: project.job_type.id,
              settings: {
                ...project.job_type.settings,
                interview_languages: [newLanguage],
                multilang_languages: newMultilangLanguages,
                language_voice: newVoice,
              },
            });
          }}
        />

        <LessonOrderSetting
          lessonOrder={project.job_type.settings.lesson_order}
          onSave={async (lessonOrder) => {
            try {
              await updateJobType({
                id: project.job_type.id,
                settings: {
                  ...project.job_type.settings,
                  lesson_order: lessonOrder,
                },
              }).unwrap();
              showToast({
                title: "Lesson order updated",
                variant: ToastVariant.Success,
              });
            } catch {
              showToast({
                title: "Failed to update lesson order",
                variant: ToastVariant.Error,
              });
            }
          }}
        />

        <InterviewRetakeSetting
          mode={interviewRetakeSettings?.mode ?? InterviewRetakeMode.Single}
          isTrainingProject
          onSave={(newMode) => {
            void updateJobType({
              id: project.job_type.id,
              settings: {
                ...project.job_type.settings,
                interview_retake_settings: {
                  ...interviewRetakeSettings,
                  mode: newMode,
                },
              },
            });
          }}
        />

        <ChapterGradingResultsEmailSetting
          isEnabled={
            project.job_type.settings
              .should_email_chapter_grading_results_to_participant
          }
          onSave={(isEnabled) => {
            void updateJobType({
              id: project.job_type.id,
              settings: {
                ...project.job_type.settings,
                should_email_chapter_grading_results_to_participant: isEnabled,
              },
            });
          }}
        />

        <AppearanceSettingsSection
          onSaveProject={(p) => void onSaveProject(p)}
          company={company}
          project={project}
        />

        <JobOpeningPreInterviewFormSetting
          project={project}
          onSaveProject={onSaveProject}
        />

        <MessagingAutomationSection project={project} />

        <ProjectNotificationsSettingsSection
          project={project}
          onSaveProject={(p) => void onSaveProject(p)}
          isTrainingProject
        />
      </div>
    </div>
  );
};

function checkHasLegacyTrainingMaterials(
  project: ProjectWithInterviewCount & ProjectWithJobType,
) {
  return project.job_type.job_description.content.some(
    (paragraph: { content: readonly unknown[] }) =>
      paragraph.content.length > 0,
  );
}
